import axiosInstance from '../../../Components/Request';

/**
 * NOVEDADES — cliente HTTP.
 *
 * ## 🔑 El texto NO se escribe aquí, y eso es lo importante del módulo
 *
 * Las novedades viven en el **backend**, en el repositorio, y llegan por `GET /me/release-notes`.
 *
 * ⚠️ Escribirlas en el frontend habría sido más directo —una constante y listo— y habría estado mal: una novedad
 * describe un **despliegue**, y el frontend y el backend se despliegan por separado. Un anuncio que dice *"ya puedes
 * filtrar los avisos por tipo"* mostrado por un frontend nuevo contra un backend viejo es una mentira que nadie puede
 * diagnosticar.
 *
 * 📌 El servidor manda también qué es nuevo para esta persona y la etiqueta del área, para que la pantalla no tenga su
 * propia copia del diccionario ni recalcule la regla de lectura.
 */

export type ReleaseArea =
  | 'notifications'
  | 'tracking'
  | 'loads'
  | 'shipments'
  | 'account'
  | 'platform';

export interface ReleaseNote {
  id: string;
  /** `YYYY-MM-DD`. Es la fecha del despliegue. */
  publishedAt: string;
  area: ReleaseArea;
  areaLabel: string;
  title: string;
  body: string;
  /** Ruta interna, si existe la pantalla de la que habla. 🔑 Es lo que convierte el anuncio en uso. */
  link?: string;
  linkLabel?: string;
  highlight?: boolean;
  /** `true` = publicada después de la última vez que esta persona marcó como leído. */
  isNew: boolean;
}

export interface ReleaseNotesPayload {
  notes: ReleaseNote[];
  unreadCount: number;
  /** `null` = nunca ha marcado nada como leído. */
  seenAt: string | null;
  /**
   * La fecha con la que hay que llamar a `markReleaseNotesSeen`.
   *
   * 🔑 **La manda el servidor a propósito**: es la fecha de lo que se acaba de mostrar. Si la pantalla mandara "ahora",
   * marcaría como leído algo publicado entre que se pintó la lista y que se pulsó el botón — una novedad que nunca se
   * vio quedaría leída.
   */
  markSeenAt: string;
}

export const getReleaseNotes = async (): Promise<ReleaseNotesPayload> => {
  const { data } = await axiosInstance.get('/me/release-notes');
  return data;
};

/**
 * Solo el número, para el globo.
 *
 * 📌 Endpoint aparte porque esto se pide en **cada** pantalla: traer la lista completa para mostrar un número sería
 * trabajo por nada, y la lista va a crecer.
 */
export const getUnreadReleaseCount = async (): Promise<number> => {
  const { data } = await axiosInstance.get('/me/release-notes/unread-count');
  return typeof data?.unread === 'number' ? data.unread : 0;
};

export interface MarkSeenResult {
  /**
   * `false` = **no quedó guardado**, porque la tabla de lectura todavía no existe en la base.
   *
   * 🔑 Se expone para poder decirlo. Un *"listo"* que no guardó nada es peor que un aviso: el usuario cierra tranquilo
   * y la campanita reaparece sin explicación.
   */
  saved: boolean;
  seenAt: string;
  message?: string;
}

export const markReleaseNotesSeen = async (seenAt: string): Promise<MarkSeenResult> => {
  const { data } = await axiosInstance.post('/me/release-notes/seen', { seenAt });
  return data;
};
